import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import decode from "jwt-decode";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { catchError, finalize, tap } from "rxjs/operators";
import { CredentialsTO } from "src/app/data/schema/Identity/CredentialsTO";
import { EnumGrantType } from "src/app/data/schema/Identity/EnumGrantType";
import { LoginResultTO } from "src/app/data/schema/Identity/LoginResultTO";
import { RecuperarSenhaTO } from "src/app/data/schema/Identity/RecuperarSenhaTO";
import { SessionTO } from "src/app/data/schema/Identity/SessionTO";
import { SubscriptionIdentityDTO } from "src/app/data/schema/Identity/SubscriptionIdentityDTO";
import { TrocarSenhaTO } from "src/app/data/schema/Identity/TrocarSenhaTO";
import { DataAnalysisService } from "src/app/data/service/data-analysis/data-analysis.service";
import { environment } from "src/environments/environment";
import { TokenPayload } from "../models/TokenPayload";
import { SubscriptionService } from "./subscription.service";

@Injectable({
    providedIn: "root"
})
export class AuthService {
    private session$: BehaviorSubject<SessionTO>;
    public sessionAsync: Observable<SessionTO>;

    constructor(
        private _subscriptionService: SubscriptionService,
        private _httpClient: HttpClient,
        private dataAnalysisService: DataAnalysisService
    ) {
        const sessionCookie = this.getCookie("session");
        if (sessionCookie != null && sessionCookie != "") {
            localStorage.setItem("session", atob(decodeURIComponent(sessionCookie)));
            this.deleteCookie("session");
        }

        let sessionCallback = this.getCookie("sessionCallback");
        if (sessionCallback != null && sessionCallback != "") {
            sessionCallback = decodeURIComponent(sessionCallback);
            if (this.isBase64(sessionCallback)) sessionCallback = atob(sessionCallback);
            sessionCallback = decodeURIComponent(sessionCallback);

            if (sessionCallback.startsWith("//")) {
                sessionCallback = sessionCallback.replace(/^\/+/, "/");
            }
            console.log(`sessionCallback=${sessionCallback}`);
            this.deleteCookie("sessionCallback");
            if (sessionCallback != location.href) window.location.href = sessionCallback;
        }

        this.session$ = new BehaviorSubject<SessionTO>(
            (JSON.parse(localStorage.getItem("session")) as SessionTO) || (null as SessionTO)
        );
        if (!this.session$.getValue()?.access_token) this.session$.next(null);

        this.sessionAsync = this.session$.asObservable();
    }

    isBase64(str: string): boolean {
        // Remover espaços em branco ou newlines
        str = str.replace(/\s/g, "");

        // Verificar se a string possui tamanho múltiplo de 4 e segue o padrão Base64
        const base64Regex = /^(?:[A-Za-z0-9+\/]{4})*(?:[A-Za-z0-9+\/]{2}==|[A-Za-z0-9+\/]{3}=)?$/;
        return base64Regex.test(str);
    }

    private deleteCookie(name) {
        this.setCookie(name, "", -1);
    }

    public setCookie(cname, cvalue, exdays) {
        const d = new Date();
        d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
        const expires = "expires=" + d.toUTCString();
        document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
    }

    private getCookie(cname) {
        const name = cname + "=";
        const ca = document.cookie.split(";");
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) == " ") {
                c = c.substring(1);
            }
            if (c.indexOf(name) == 0) {
                return c.substring(name.length, c.length);
            }
        }
        return null;
    }

    public isAuthenticated = (): boolean => {
        const userId: string = this.getClaimsUserId();
        return userId !== undefined && userId !== null && !this.session$?.value?.mfa_required;
    };

    private login(credentialsTO: CredentialsTO): Observable<SessionTO> {
        return this._httpClient.post<SessionTO>(
            `${environment.identity.url}/api/v1/Auth/login`,
            JSON.stringify(credentialsTO),
            {
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                }
            }
        );
    }

    listSubscriptionByUsername(email: string): Observable<SubscriptionIdentityDTO[]> {
        return this._httpClient.get<SubscriptionIdentityDTO[]>(
            `${environment.identity.url}/api/v1/subscriptions?login=${btoa(email)}`,
            {
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                }
            }
        );
    }

    loginPassword(username: string, password: string, subscriptionId: string): Observable<LoginResultTO> {
        return new Observable<LoginResultTO>((observable) =>
            this.login({
                grant_type: EnumGrantType.password,
                username: username.toLowerCase(),
                password,
                client_id: environment.identity.client_id,
                subscriptionId: subscriptionId || environment.default_subscription_id
            } as CredentialsTO)
                .pipe(
                    tap((session) => {
                        this.setSession(session);

                        if (!session.mfa_required)
                            this.dataAnalysisService.login(username, [...username], this.getClaimsName());

                        observable.next({
                            success: !session.mfa_required,
                            requiredMfa: session.mfa_required
                        } as LoginResultTO);
                    })
                )
                .subscribe(
                    () => observable.complete(),
                    (err) => observable.error(err)
                )
        );
    }

    loginMfaCode(username: string, code: string, subscriptionId: string): Observable<LoginResultTO> {
        return new Observable<LoginResultTO>((observable) =>
            this.login({
                grant_type: EnumGrantType.mfa,
                client_id: environment.identity.client_id,
                subscriptionId: subscriptionId || environment.default_subscription_id,
                mfa_code: code,
                mfa_token: this.session$.value?.mfa_token
            } as CredentialsTO)
                .pipe(
                    tap((session) => {
                        this.setSession(session);
                        this.dataAnalysisService.login(username, [...username], this.getClaimsName());

                        observable.next({
                            success: true
                        } as LoginResultTO);
                    })
                )
                .subscribe(
                    () => observable.complete(),
                    (err) => observable.error(err)
                )
        );
    }

    loginThirdparty(username: string, password: string, subscriptionId: string): Observable<LoginResultTO> {
        return new Observable<LoginResultTO>((observable) =>
            this.login({
                grant_type: EnumGrantType.thirdparty_token,
                username: username.toLowerCase(),
                thirdpartyToken: password,
                client_id: environment.identity.client_id,
                subscriptionId: subscriptionId || environment.default_subscription_id
            } as CredentialsTO)
                .pipe(
                    tap((session) => {
                        session.createDate = new Date();
                        this.session$.next(session);
                        localStorage.setItem("session", JSON.stringify(this.session$.getValue()));
                        this._subscriptionService.setCurrentSubscriptionId(
                            subscriptionId || environment.default_subscription_id
                        );
                        observable.next({
                            success: true
                        } as LoginResultTO);
                    }),
                    finalize(() => {
                        observable.complete();
                    }),
                    catchError((error) => of(observable.error(error)))
                )
                .subscribe()
        );
    }

    requestPasswordReset(username: string, subscriptionId: string): Observable<any> {
        const recuperarSenhaTO: RecuperarSenhaTO = {
            subscriptionId: subscriptionId || this.getCurrentSubscriptionId(),
            username: username.toLowerCase()
        };

        return this._httpClient.post<SessionTO>(
            `${environment.identity.url}/api/v1/user/requestPasswordReset`,
            JSON.stringify(recuperarSenhaTO),
            {
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                }
            }
        );
    }

    trocarSenha(token: string, novaSenha: string, subscriptionId: string): Observable<any> {
        const trocarSenhaTO: TrocarSenhaTO = {
            subscriptionId: subscriptionId || this.getCurrentSubscriptionId(),
            token: token,
            newPassword: novaSenha
        };
        return this._httpClient.patch<SessionTO>(
            `${environment.identity.url}/api/v1/user/password`,
            JSON.stringify(trocarSenhaTO),
            {
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                }
            }
        );
    }

    changePasswordLogged(oldPassword: string, newPassword: string): Observable<any> {
        return this._httpClient.patch<any>(
            `${environment.identity.url}/api/v1/user/${this.getClaimsUserId()}/password`,
            JSON.stringify({ newPassword: newPassword, oldPassword: oldPassword }),
            {
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                }
            }
        );
    }

    logout(): Observable<boolean> {
        return new Observable<boolean>((observable) => {
            this.session$.next(null);
            localStorage.clear();
            this._subscriptionService.setAppLanguage();

            observable.next(true);
            observable.complete();
        });
    }

    getDateEx(): Date {
        return new Date(
            new Date(this.session$.getValue().createDate).getTime() + this.session$.getValue().expires_in * 1000
        );
    }

    getAccessToken(): string {
        if (!this.session$.getValue()?.access_token) {
            return null;
        }

        return this.session$.getValue()?.access_token;
    }

    getDecodedToken(): TokenPayload | null {
        const token = this.getAccessToken();

        if (!token) return null;

        const tokenPayload: TokenPayload = decode(token) as TokenPayload;
        return tokenPayload;
    }

    getClaimsUserId(): string {
        const decodedClaims = this.getDecodedClaimsFromSession();

        if (decodedClaims) return decodedClaims.userId;

        return null;
    }

    getClaimsName(): string {
        const decodedClaims = this.getDecodedClaimsFromSession();

        if (decodedClaims) return decodedClaims.name;

        return null;
    }

    getClaimsRoles(): string[] {
        const token = this.getDecodedToken();

        if (!token) return [];

        return token.roles as string[];
    }

    hasAnyRole(roles: string[]): boolean {
        const userRoles = this.getClaimsRoles();
        return roles?.some((role) => userRoles?.includes(role)) || false;
    }

    getClaimsGroupIds(): string[] {
        const token = this.getDecodedToken();

        if (!token) return [];

        if (typeof token.groups === "string") {
            const groups: string[] = [];
            groups.push(token.groups);
            return groups;
        }

        return token.groups as string[];
    }

    getClaimsPlanIds(): string[] {
        const token = this.getDecodedToken();

        if (!token) return [];

        if (typeof token.plans === "string") {
            const plans: string[] = [];
            plans.push(token.plans);
            return plans;
        }

        return token.plans as string[];
    }

    public setLastLogin(): void {
        localStorage.setItem(`lastaccess_${this.getClaimsUserId()}`, Date.now().toString());
    }

    public triggerUserMatch(): boolean {
        const lastAccess = new Date(localStorage.getItem(`lastaccess_${this.getClaimsUserId()}`));
        lastAccess.setDate(lastAccess.getDate() + 1);

        return lastAccess.getDate() < Date.now();
    }

    private getDecodedClaimsFromSession = (): any => {
        // this.session$.next(JSON.parse(localStorage.getItem("session")) as SessionTO);

        if (this.session$.getValue()?.access_token == null) {
            return null;
        }

        const base64Url = this.session$.getValue().access_token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split("")
                .map(function (c) {
                    return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
                })
                .join("")
        );

        return JSON.parse(jsonPayload);
    };

    getClaimsSub(): string {
        const decodedClaims = this.getDecodedClaimsFromSession();

        if (decodedClaims) return decodedClaims.sub;

        return null;
    }

    getClaimsUserName(): string {
        const decodedClaims = this.getDecodedClaimsFromSession();

        if (decodedClaims) return decodedClaims.username;

        return null;
    }

    public getCurrentSubscriptionId = (): string => {
        let currentSubscriptionId = localStorage.getItem(`currentSubscriptionId_${location.hostname}`);

        if (!currentSubscriptionId)
            this._subscriptionService.currentSubscription$.subscribe(
                (subscription) => (currentSubscriptionId = subscription.id)
            );

        return currentSubscriptionId;
    };

    private getClaimsSubscriptionId = (): string => {
        const decodedClaims = this.getDecodedClaimsFromSession();

        if (decodedClaims) return decodedClaims.subscriptionId;

        return null;
    };

    private refreshToken(subscriptionId: string, forceRefreshData: boolean): Observable<SessionTO> {
        if (!this.session$.getValue()) return throwError(new Error("Session invalid"));

        const credentialsTO = {
            grant_type: EnumGrantType.refresh_token,
            refresh_token: this.session$.getValue().refresh_token,
            client_id: environment.identity.client_id,
            subscriptionId: subscriptionId || environment.default_subscription_id,
            refresh_token_force: forceRefreshData
        };

        return this._httpClient.post<SessionTO>(
            `${environment.identity.url}/api/v1/Auth/login`,
            JSON.stringify(credentialsTO),
            {
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                }
            }
        );
    }

    public applyRefreshToken(subscriptionId: string, forceRefreshData: boolean): Observable<LoginResultTO> {
        return new Observable<LoginResultTO>((observable) =>
            this.refreshToken(subscriptionId, forceRefreshData)
                .pipe(
                    tap((session) => {
                        if (session) {
                            session.createDate = new Date();
                            this.session$.next(session);

                            localStorage.setItem("session", JSON.stringify(session));

                            observable.next({
                                success: true
                            } as LoginResultTO);
                        } else {
                            this.session$.next(null);
                            localStorage.clear();
                            this._subscriptionService.setAppLanguage();

                            observable.next({
                                success: false,
                                message: "Token expired :("
                            } as LoginResultTO);
                        }
                    }),
                    catchError((error) =>
                        of(
                            observable.next({
                                success: false,
                                message: error
                            } as LoginResultTO)
                        )
                    )
                )
                .subscribe()
        );
    }

    public setSession(session: SessionTO): void {
        session.createDate = new Date();
        this.session$.next(session);
        localStorage.setItem("session", JSON.stringify(session));
    }
}
